import React, { useState, useEffect, useRef } from "react";
import { auth } from "../firebaseConfig";
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  onAuthStateChanged,
} from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { motion } from "framer-motion";
import "./SignIn.css";

import aiWebBackground from "../assets/ai-web-background.png";

import { logAction } from "../utils/analytics";
import { detect } from "detect-browser";
import UAParser from "ua-parser-js";

const SECURITY_CONFIG = {
  failedAttemptsThreshold: 5,
  resetRequestsThreshold: 3,
  timeBetweenAttempts: 30000,
};

const SignIn = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [securityCheck, setSecurityCheck] = useState(null);

  const navigate = useNavigate();
  const failedAttempts = useRef({});
  const resetRequests = useRef({});

  const getDeviceInfo = () => {
    const browser = detect();
    const parser = new UAParser();
    const ua = parser.getResult();

    return {
      browser: `${browser?.name || "unknown"} ${browser?.version || ""}`,
      os: ua.os.name,
      deviceType: ua.device.type || "desktop",
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      cookiesEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack === "1",
      ipAddress: "",
    };
  };

  const checkSecurityPatterns = (email, action) => {
    const now = Date.now();
    const emailKey = email.toLowerCase();

    if (action === "failed") {
      if (!failedAttempts.current[emailKey]) {
        failedAttempts.current[emailKey] = { count: 0, timestamps: [] };
      }

      failedAttempts.current[emailKey].count++;
      failedAttempts.current[emailKey].timestamps.push(now);

      if (
        failedAttempts.current[emailKey].count >=
        SECURITY_CONFIG.failedAttemptsThreshold
      ) {
        setSecurityCheck(
          "We noticed several failed attempts. Please wait a moment or reset your password if needed."
        );
        return true;
      }

      const recentAttempts = failedAttempts.current[emailKey].timestamps.filter(
        (timestamp) => now - timestamp < SECURITY_CONFIG.timeBetweenAttempts
      );

      if (recentAttempts.length > 2) {
        setSecurityCheck("Too many attempts too quickly. Please wait 30 seconds.");
        return true;
      }
    }

    if (action === "reset") {
      if (!resetRequests.current[emailKey]) {
        resetRequests.current[emailKey] = { count: 0, timestamps: [] };
      }

      resetRequests.current[emailKey].count++;
      resetRequests.current[emailKey].timestamps.push(now);

      if (
        resetRequests.current[emailKey].count >=
        SECURITY_CONFIG.resetRequestsThreshold
      ) {
        setSecurityCheck(
          "Multiple password reset requests detected. Check your email or contact support."
        );
        return true;
      }
    }

    return false;
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const verifyRes = await fetch(
            `/api/verification/check-verification?email=${encodeURIComponent(user.email)}`
          );

          const verifyData = await verifyRes.json();

          if (verifyData?.role) {
            localStorage.setItem("haske_role", verifyData.role);
            document.cookie = `haske_role=${verifyData.role}; path=/; SameSite=Lax`;
          }
        } catch (e) {
          console.error("Auto sign-in role check failed:", e);
        }

        logAction("User Auto-Sign In", { method: "session" }, user, getDeviceInfo());
        navigate("/patient-details");
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const handleSignIn = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      setError("Email and password are required.");
      return;
    }

    if (checkSecurityPatterns(email, "failed")) {
      setError("Security check triggered. Please follow the instructions.");
      return;
    }

    setLoading(true);
    setError(null);
    setMessage(null);
    setSecurityCheck(null);

    try {
      const startTime = Date.now();
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      logAction(
        "User Sign In",
        {
          method: "email",
          signInDuration: Date.now() - startTime,
        },
        user,
        getDeviceInfo()
      );

      const emailKey = email.toLowerCase();
      failedAttempts.current[emailKey] = { count: 0, timestamps: [] };

      // Get role from your Haske server
      const verifyRes = await fetch(
        `/api/verification/check-verification?email=${encodeURIComponent(email)}`
      );

      const verifyData = await verifyRes.json();

      if (verifyData?.role) {
        localStorage.setItem("haske_role", verifyData.role);

        document.cookie =
          `haske_role=${verifyData.role}; path=/`;
        }

            navigate("/patient-details");

          } catch (error) {
            // Firebase failed. Try local Haske login for offline/local hospital use.
            try {
              const localRes = await fetch("/api/auth/login", {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({ email, password }),
              });

              const localData = await localRes.json();

              if (!localRes.ok || !localData.success) {
                throw new Error(localData.message || "Local login failed");
              }

              localStorage.setItem("haske_role", localData.user.role);
              document.cookie = `haske_role=${localData.user.role}; path=/; SameSite=Lax`;

              navigate("/patient-details");
              return;

            } catch (localError) {
              setError("Invalid email or password. Please try again.");
            }

            logAction(
              "Sign In Failed",
              {
                email,
                errorCode: error.code,
                errorMessage: error.message,
              },
            null,
            getDeviceInfo()
          );

        } finally {
          setLoading(false);
        }
      };

  const handlePasswordReset = async () => {
    if (!email) {
      setError("Please enter your email address first.");
      return;
    }

    if (checkSecurityPatterns(email, "reset")) {
      setError("Security check triggered. Please follow the instructions.");
      return;
    }

    try {
      await sendPasswordResetEmail(auth, email);
      setMessage("Password reset email sent. Please check your inbox.");
      setError(null);
    } catch (error) {
      setError(`Error sending reset email: ${error.message}`);
    }
  };

  return (
    <div className="signin-container">
      <div className="left-column">
        <div
          className="background-image-wrapper"
          style={{
            backgroundImage: `url(${aiWebBackground})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
      </div>

      <div className="right-column">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="form-wrapper"
        >
          <div className="logo-row">
            <img src="/umth.png" alt="UMTH" className="hospital-logo" />
            <img src="/haske.png" alt="Haske" className="haske-logo" />
          </div>

          <h2 className="form-title">Welcome to UMTH PACS</h2>
          <p className="form-subtitle">
            Secure AI-enabled medical imaging platform
          </p>

          {securityCheck && (
            <div className="security-alert">
              <p>{securityCheck}</p>
            </div>
          )}

          <form className="signin-form" onSubmit={handleSignIn}>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email Address"
              required
              className="signin-input"
            />

            <div className="password-input-container">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                required
                className="signin-input"
              />

              <button
                type="button"
                className="password-toggle-button"
                onClick={() => setShowPassword((prev) => !prev)}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              type="submit"
              className="signin-button"
              disabled={loading}
            >
              {loading ? <div className="spinner"></div> : "Sign In"}
            </motion.button>
          </form>

          {error && <p className="signin-error">{error}</p>}
          {message && <p className="signin-message">{message}</p>}

          <p className="signin-footer">
            New on our platform? <a href="/register">Create an account</a>
          </p>

          <p className="forgot-password-link" onClick={handlePasswordReset}>
            Forgot Password?
          </p>

          <div className="support-footer">
            <p className="support-title">For complaints and enquiries</p>

            <a
              href="mailto:feedback@umth.haske.online"
              className="support-email"
            >
              ✉ feedbackumth@haske.online
            </a>

          </div>
          <p className="powered-by">Powered by MAILAB</p>
        </motion.div>
      </div>
    </div>
  );
};

export default SignIn;